import type { OneOffTaskEntity } from '@/api/entity_types/OneOffTask';
import type { ChecklistTaskEntity } from '@/api/entity_types/ChecklistTask';
import type { GetContactTaskResponse } from '@/api/contacts';
import type { UserTaskProductArea } from '@/api/entity_types/UserTask';
import { useCurrentUserStore } from '@/stores/currentUser.ts';
import DateUtility from '@/utilities/dates';
import type { AssigneeOption, TaskTypeOption } from './filterConfiguration';

export type TaskStatus = 'Complete' | 'Incomplete';

export type OverviewTaskItem = {
  userTaskId: number;
  userTask: GetContactTaskResponse;
  name: string;
  assigneeName: string;
  dueDateString: string;
  dueDate: Date | null;
  taskTypeOption: TaskTypeOption;
  statusOption: TaskStatus;
  assigneeOption: AssigneeOption;
  productArea: UserTaskProductArea;
  canCompleteFromOverview: boolean;
  completionBlockedReason: string | null;
};

export const useOverviewTaskParser = () => {
  const currentUserStore = useCurrentUserStore();
  const loggedInUsername = currentUserStore.currentUserAccountDetails.username;
  const parseContactTasks = (contactTasks: GetContactTaskResponse[]): OverviewTaskItem[] => {
    return contactTasks.map(contactTask => {
      const statusOption: TaskStatus = contactTask.status === 'completed' ? 'Complete' : 'Incomplete';
      const { dueDate, dueDateString } = parseDate(contactTask);
      const { canComplete, blockedReason } = parseCompletionEligibility(contactTask);

      const overviewTaskItem: OverviewTaskItem = {
        userTaskId: contactTask.id,
        userTask: contactTask,
        name: contactTask.name,
        assigneeName: contactTask.user.name?.trim() || contactTask.user.email.split('@')[0],
        productArea: contactTask.product_area,
        dueDateString,
        dueDate,
        taskTypeOption: parseTaskType(contactTask),
        statusOption,
        assigneeOption: parseAssigneeOption(contactTask, loggedInUsername),
        canCompleteFromOverview: canComplete,
        completionBlockedReason: blockedReason,
      };
      return overviewTaskItem;
    });
  };

  return { parseContactTasks };
};

const parseDate = (contactTask: GetContactTaskResponse) => {
  const noDate = { dueDateString: '—', dueDate: null };

  if (contactTask.taskable_type !== 'OneOffTask' || !contactTask.taskable) return noDate;
  const dueDate = DateUtility.parseLocalDate((contactTask.taskable as OneOffTaskEntity).due_date);
  if (!dueDate) return noDate;

  return {
    dueDateString: dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    dueDate,
  };
};

const parseTaskType = (contactTask: GetContactTaskResponse): TaskTypeOption => {
  if (contactTask.taskable_type === 'OneOffTask') return 'One Off';
  if (contactTask.product_area === 'offboarding') return 'Offboarding';
  if (contactTask.product_area === 'training') return 'Training';
  return 'Onboarding';
};

const parseAssigneeOption = (contactTask: GetContactTaskResponse, loggedInUsername: string): AssigneeOption => {
  if (!contactTask.context) return 'Agent';

  return loggedInUsername === contactTask.user.email ? 'Me' : 'Others';
};

const COMPLETABLE_CHECKLIST_TYPES = ['ChecklistBasicTask', 'ChecklistEmbedTask'];

const TASK_ACTION_LABELS: Record<string, string> = {
  ChecklistUploadTask: 'upload a file',
  ChecklistEsignatureTask: 'provide a signature',
  ChecklistOnboardingFormTask: 'submit a form',
};

const parseCompletionEligibility = (
  contactTask: GetContactTaskResponse
): { canComplete: boolean; blockedReason: string | null } => {
  if (contactTask.taskable_type === 'OneOffTask') {
    return { canComplete: true, blockedReason: null };
  }

  const checklistTask = contactTask.taskable as ChecklistTaskEntity;
  const taskableType = checklistTask.checklist_taskable_type;

  if (COMPLETABLE_CHECKLIST_TYPES.includes(taskableType)) {
    return { canComplete: true, blockedReason: null };
  }

  const action = TASK_ACTION_LABELS[taskableType] || 'take action';
  return {
    canComplete: false,
    blockedReason: `Assignee needs to ${action} to complete this task`,
  };
};
